import { html, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './image-grid.styles';

import { register } from 'swiper/element/bundle';
import { DERElement } from '~/common/der-element';
import '~/ui/components/controls';
import '~/ui/components/controls/enum';
import { DerVariant, Direction } from '~/ui/components/controls/enum';

type ImageArray = { url: string; alt: string }[];

export class ImageGrid extends DERElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: Array })
    images: ImageArray = [];

    @state()
    private isBeginning = true;

    @state()
    private isEnd = false;

    private swiperEl: HTMLElement | null = null;

    constructor() {
        super();
        this.checkMobile();
    }

    override updated() {
        this._registerSwiper();
    }

    private _registerSwiper() {
        this.swiperEl = this.shadowRoot?.querySelector('swiper-container') as HTMLElement | null;
        if (!this.isSmallDevice || !this.swiperEl || !!('swiper' in this.swiperEl)) {
            return;
        }
        register();
        if (this.swiperEl) {
            Object.assign(this.swiperEl, {
                navigation: {
                    nextEl: '.custom-next',
                    prevEl: '.custom-prev',
                },
                on: {
                    init: () => this.updateButtonStates(),
                    slideChange: () => this.updateButtonStates(),
                },
            });
            (this.swiperEl as unknown as { initialize: () => void }).initialize();
        }
    }

    private updateButtonStates() {
        if (!this.swiperEl) return;

        this.isBeginning =
            (
                this.swiperEl as HTMLElement & {
                    swiper?: { isBeginning: boolean };
                }
            ).swiper?.isBeginning ?? true;
        this.isEnd = (this.swiperEl as HTMLElement & { swiper?: { isEnd: boolean } }).swiper?.isEnd ?? false;

        // Force update for lit-element
        this.requestUpdate();
    }

    renderImageWrapper(isSmallDevice: boolean) {
        if (!this.images) {
            return html``;
        } else if (isSmallDevice) {
            return html`<div class="mobile-view">
                <swiper-container class="image-carousel" .loop=${false} slides-per-view="1" .init=${false}>
                    ${this.images.map(
                        (img) => html`
                            <swiper-slide>
                                <div class="swiper-slide-content">
                                    <img src=${img.url} alt=${img.alt} />
                                </div>
                            </swiper-slide>
                        `
                    )}
                    ${this.images?.length > 1
                        ? html`
                              <div slot="container-end" class="swiper-buttons-container">
                                  <der-ui-controls
                                      variant=${DerVariant.Reduced}
                                      direction=${Direction.Left}
                                      class="custom-prev"
                                      aria-label="Previous slide"
                                      ?disabled=${this.isBeginning}
                                  ></der-ui-controls>
                                  <der-ui-controls
                                      variant=${DerVariant.Reduced}
                                      direction=${Direction.Right}
                                      class="custom-next"
                                      aria-label="Next slide"
                                      ?disabled=${this.isEnd}
                                  ></der-ui-controls>
                              </div>
                          `
                        : null}
                </swiper-container>
            </div>`;
        } else {
            return html`<div class="o-image-grid desktop-view">
                ${this.images.map(
                    (img) => html`
                        <div class="o-image-grid__item">
                            <img src=${img.url} alt=${img.alt} />
                        </div>
                    `
                )}
            </div>`;
        }
    }

    override render() {
        return this.smallDeviceTask.render({
            complete: (isSmallDevice: boolean) => html`
                <div class="container">${this.renderImageWrapper(isSmallDevice)}</div>
            `,
        });
    }
}
